import React from 'react';
import { money } from './format';
import { groupTxByDay } from './dataAdapters';
import { MerchantIcon } from './icons';
import { dayLabel } from './format';
import { signedAmount } from '../lib/txType';

export function Money({ value, sign = false, className = '', style }) {
  // There is no positive colour left to reach for: money-in is signalled by
  // the + sign and the tabular column it lands in, not a hue. --pos is ink.
  const positive = value > 0;
  return (
    <span
      className={`num ${className}`}
      style={{ color: sign && positive ? 'var(--pos)' : undefined, ...style }}
    >
      {money(value, { sign })}
    </span>
  );
}

export function ChipFilter({ options, value, onChange, size = 'md' }) {
  const sm = size === 'sm';
  return (
    <div style={{ display: 'flex', gap: sm ? 6 : 8, flexWrap: 'wrap' }}>
      {options.map((o) => {
        const v = o.value ?? o;
        const label = o.label ?? o;
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`chip pressable ${active ? 'is-active' : ''}`}
            style={{ cursor: 'pointer', fontSize: 12, height: sm ? 30 : 34, padding: sm ? '0 12px' : '0 15px' }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function Segmented({ options, value, onChange, size = 'md' }) {
  const h = size === 'sm' ? 32 : 38;
  return (
    <div style={{ display: 'inline-flex', background: 'var(--surface-3)', borderRadius: 9, padding: 3, gap: 2 }}>
      {options.map((o) => {
        const v = o.value ?? o;
        const label = o.label ?? o;
        const active = v === value;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            className="pressable"
            style={{
              height: h,
              padding: '0 14px',
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 500,
              color: active ? 'var(--bg)' : 'var(--ink-2)',
              background: active ? 'var(--ink)' : 'transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function TxRow({ tx, onClick, showDate = true, accountName, table }) {
  // Table form is the ledger: one tabular column per fact, money right-aligned
  // so the eye runs straight down the amounts. Category and account drop to
  // secondary weight — they are context, not the point — and the merchant
  // icon goes entirely, since a grid of logos is decoration in a dense list.
  if (table) {
    return (
      <button onClick={onClick} className="row-line pressable" style={{ gap: 16 }}>
        <span className="num" style={{ width: 52, flexShrink: 0, fontSize: 12, color: 'var(--ink-3)' }}>
          {dayLabel(tx.date)}
        </span>
        <span style={{ width: 250, flexShrink: 0, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tx.desc}
          {tx.pending && (
            <span className="chip" style={{ marginLeft: 8, height: 20, fontSize: 10.5, padding: '0 6px', color: 'var(--ink-3)' }}>Pending</span>
          )}
        </span>
        <span style={{ width: 150, flexShrink: 0, fontSize: 13, color: 'var(--ink-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tx.cat}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {accountName}
          {tx.recurring && <span style={{ marginLeft: accountName ? 8 : 0 }}>· Recurring</span>}
        </span>
        <Money value={signedAmount(tx)} sign style={{ fontSize: 14.5, fontWeight: 600, textAlign: 'right' }} />
      </button>
    );
  }

  return (
    <button onClick={onClick} className="row-line pressable" style={{ alignItems: 'center' }}>
      <MerchantIcon logoUrl={tx.logoUrl} website={tx.website} cat={tx.cat} size={36} radius={9} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tx.desc}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {tx.cat && <span>{tx.cat}</span>}
          {tx.cat && (accountName || (showDate && dayLabel(tx.date))) && <span aria-hidden>·</span>}
          {accountName || (showDate && dayLabel(tx.date))}
          {tx.recurring && <><span aria-hidden>·</span><span>Recurring</span></>}
          {tx.pending && <><span aria-hidden>·</span><span>Pending</span></>}
        </div>
      </div>
      <Money value={signedAmount(tx)} sign style={{ fontSize: 14.5, fontWeight: 600 }} />
    </button>
  );
}

export function TxList({ items, onPick, max, accountNames = {}, table = false }) {
  const groups = groupTxByDay(items);
  const shown = max ? groups.slice(0, max) : groups;

  // Both forms are hairline rows now; the table form adds the tabular columns
  // and puts the day total on the group rule instead of in a header strip.
  const groupGap = table ? 26 : 22;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: groupGap }}>
      {shown.map((g) => (
        <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="pg-head">
            <span className="eyebrow">{g.label}</span>
            <Money value={g.total} style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {g.items.map((t) => (
              <TxRow
                key={t.id}
                tx={t}
                table={table}
                showDate={false}
                accountName={table ? (accountNames[t.raw?.accountId] || t.raw?.accountName) : undefined}
                onClick={() => onPick && onPick(t)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
